import { useMutation, useSuspenseQuery } from '@apollo/client';
import { useState } from 'react';

import {
  Button,
  Icon,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
  useToast,
} from '@eluve/components';
import { DbEnums } from '@eluve/graphql-types';
import { graphql } from '@eluve/graphql.tada';
import { useReviewHasuraContext } from '@eluve/session-helpers';

import { useAppointmentParams } from '../useAppointmentParams';

import { useGoldenSummaryContext } from './GoldenSummaryContextProvider';
import {
  createGoldenSummaryMutation,
  finalizeGoldenSummaryMutation,
} from './operations';

interface GoldenSummarySelectProps {
  onSelectSummary: (summaryId: string) => void;
  originalContent?: Record<string, unknown>;
}

const listGoldenSummaries = graphql(`
  query getGoldenNoteSummary($tenantId: uuid!, $appointmentId: uuid!) {
    appointmentsByPk(tenantId: $tenantId, id: $appointmentId) {
      __typename
      id
      mode
      appointment_golden_notes(orderBy: { createdAt: DESC }) {
        __typename
        id
        content
        status
        createdAt
        updatedAt
      }
    }
  }
`);

export const GoldenSummarySelect = ({
  onSelectSummary,
  originalContent,
}: GoldenSummarySelectProps) => {
  const { tenantId, appointmentId } = useAppointmentParams();
  const [activeNoteId, setActiveNoteId] = useState<string | null>(null);
  const { hasPendingChanges } = useGoldenSummaryContext();
  const toast = useToast();
  const context = useReviewHasuraContext();

  const { data, refetch } = useSuspenseQuery(listGoldenSummaries, {
    context,
    variables: { tenantId, appointmentId },
  });
  const goldenNotes = data?.appointmentsByPk?.appointment_golden_notes ?? [];
  const activeNote = goldenNotes.find((note) => note.id === activeNoteId);

  const [createGoldenSummary] = useMutation(createGoldenSummaryMutation);
  const [finalizeGoldenSummary] = useMutation(finalizeGoldenSummaryMutation);

  const handleCreateGoldenSummary = async () => {
    const result = await createGoldenSummary({
      variables: {
        appointmentId,
        tenantId,
        outputType: DbEnums.LlmOutputType.SOAP_NOTE,
        content: originalContent ?? {},
      },
    });
    if (result.data?.insertAppointmentGoldenNotesOne?.id) {
      toast.success('Golden summary created');
      await refetch();
      setActiveNoteId(result.data.insertAppointmentGoldenNotesOne.id);
      onSelectSummary(result.data.insertAppointmentGoldenNotesOne.id);
    }
  };

  const handleFinalizeGoldenSummary = async () => {
    if (!activeNoteId) return;
    await finalizeGoldenSummary({
      variables: {
        id: activeNoteId,
        tenantId,
      },
    });
    toast.success('Golden summary finalized');
  };
  return (
    <Stack
      gap={2}
      className="py-1.5"
      direction="horizontal"
      justify="between"
      wrap
    >
      <Select
        value={activeNoteId ?? ''}
        disabled={hasPendingChanges || goldenNotes.length === 0}
        onValueChange={(noteId: string) => {
          setActiveNoteId(noteId);
          onSelectSummary(noteId);
        }}
      >
        <SelectTrigger className="w-auto min-w-0 grow rounded-lg p-3">
          <SelectValue placeholder="Select a version" />
        </SelectTrigger>
        <SelectContent className="rounded-lg">
          {goldenNotes.map((note) => (
            <SelectItem key={note.id} value={note.id}>
              {new Date(note.createdAt).toLocaleString()} ({note.status})
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Stack
        className="shrink-0"
        fullWidth={false}
        gap={2}
        direction="horizontal"
      >
        {activeNote && activeNote.status !== 'COMPLETED' && (
          <Button
            onClick={handleFinalizeGoldenSummary}
            loading={hasPendingChanges}
          >
            <Icon name="CheckSquare" size="auto" />
            Finalize
          </Button>
        )}
        <Button onClick={handleCreateGoldenSummary} loading={hasPendingChanges}>
          <Icon name="PlusCircle" size="auto" />
          New Golden Summary
        </Button>
      </Stack>
    </Stack>
  );
};
